import React, { useEffect, useState } from "react";
import { Shield, X } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

// One-shot celebratory banner shown on next app open after a streak freeze saved a run.
// Clears the server flag immediately so it only ever appears once.
export function FreezeBanner() {
  const { user } = useAuth();
  const pending = user?.freeze_saved_pending;
  const [saved, setSaved] = useState(null); // captured {streak} so the banner survives flag clearing
  const [show, setShow] = useState(false);

  // The banner shows on a cold app load (AuthProvider already fetches /auth/me, which
  // carries the flag). We intentionally do NOT force an extra refresh here — that created
  // a second `user` object change on every mount and churned the WebSocket reconnect.
  useEffect(() => {
    if (!pending) return;
    setSaved(pending);
    setShow(true);
    api.post("/streak/freeze-seen").catch(() => {}); // clear the server flag so it won't show again
    const t = setTimeout(() => setShow(false), 9000);
    return () => clearTimeout(t);
  }, [pending]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!show || !saved) return null;

  return (
    <div className="fixed top-[calc(5rem+env(safe-area-inset-top))] left-2 right-2 z-[60] mx-auto max-w-md" data-testid="freeze-saved-banner">
      <div className="glass border border-sky-500/40 rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3 animate-slide-up">
        <div className="w-10 h-10 rounded-xl bg-sky-500/15 border border-sky-500/30 flex items-center justify-center shrink-0">
          <Shield className="w-5 h-5 text-sky-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-100">Your streak was saved! 🛡️</div>
          <div className="text-xs text-slate-400">A freeze token kept your <span className="text-sky-300 font-semibold">{saved.streak}-day</span> howl streak alive. Howl today to keep it rolling.</div>
        </div>
        <button onClick={() => setShow(false)} data-testid="freeze-banner-dismiss" className="shrink-0 text-slate-500 hover:text-slate-300" title="Dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
